import React from 'react';
import {
	Checkbox,
	Col,
	ConfigProvider,
	Empty,
	Form,
	Input,
	Row,
} from 'antd';
import {
	CargoContractSides,
	CargoUnitLabels,
	EstimateStatus,
	LaytimeTerms,
	LinerCostUnits,
} from '@shared/utils/constants';
import { round } from '@shared/utils/math';
import { getDemurrageTermLabel } from '@shared/utils/demurrageTerm';
import type { GetCargoDetailsResponse } from '@api/features/cargos/getCargoDetails';
import type { EditableCargo } from '@api/features/cargos/updateCargo';
import type { CargoPortWithEstimatedValues } from '@api/features/cargos/transformCargos';
import NumericInput from '@client/components/NumericInput';
import MultiCurrencyInput from '@client/components/MultiCurrencyInput';
import Select from '@client/components/Select';
import styles from './CargosListScreen.module.css';

const EstimatedCargoPortForm = ({
	cargo,
	cargoPort,
	handleCargoPortChange,
}: {
	cargo: EditableCargo;
	cargoPort: GetCargoDetailsResponse['cargoPorts'][number];
	refreshCargoDetails: () => void;
	handleCargoPortChange: (values: {
		cargoPortId: number; cargoId: number; attributes: any;
	}
	) => void;
}) => {
	const disabled = cargo.status === EstimateStatus.Fixed && cargo.voyageId == null;
	const { componentDisabled } = ConfigProvider.useConfig();
	const demurrageWord = getDemurrageTermLabel(cargo.demurrageTerm);
	const hasCargoRelet = cargo.contractSide === CargoContractSides.VC_IN;
	const handleUpdate = (fieldName: keyof CargoPortWithEstimatedValues, value: number | null) => {
		handleCargoPortChange({
			cargoPortId: cargoPort.id,
			cargoId: cargo.id,
			attributes: {
				[fieldName]: value,
			},
		});
	};

	const cargoUnit = cargoPort.linerCostUnit ?? cargo.unit;

	if (cargoPort == null) {
		return (<Empty description="No cargo-port selected" />);
	}

	/*
	* NOTE: You may be wondering - why "valuePropName=v" on all the form items components?
	* Thats to avoid Form.Item from overriding the value on the nested input element.
	* We'd like to control the value ourselves but still keep the label stuff from Form.Item
	*/
	return (
		<>
			<Row gutter={[16, 0]}>
				{cargo.laytimeTerms === LaytimeTerms.NON_REVERSIBLE && (
					<>
						<Col span={12}>
							<Form.Item
								name="estimateDemurrage"
								label={`Estimate ${demurrageWord}`}
								labelCol={{ span: 24 }}
							>
								<Checkbox
									disabled={cargo.laytimeTerms !== LaytimeTerms.NON_REVERSIBLE || componentDisabled}
									key={cargoPort.id}
									checked={cargoPort.estimateDemurrage}
									onChange={(value) => {
										handleCargoPortChange({
											cargoPortId: cargoPort.id,
											cargoId: cargo.id,
											attributes: {
												estimateDemurrage: value.target.checked,
											}
										});
									}}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="estimatedTimeUsed"
								label="Est. Time Used"
								valuePropName="v"
								colon={false}
								className={styles.formItemLabel}
								labelCol={{ span: 24 }}
							>
								<NumericInput
									key={cargoPort.id}
									disabled={cargo.laytimeTerms !== LaytimeTerms.NON_REVERSIBLE}
									placeholder="Est. Time Used"
									addonAfter={`${cargoPort.laytimeInterval}s`}
									value={round(cargoPort.estimatedTimeUsed ?? 0, 6)}
									onChange={(value) => handleUpdate('estimatedTimeUsed', value)}
								/>
							</Form.Item>
						</Col>
						{(cargoPort.estimatedDespatch > 0 || cargoPort.estimatedDemurrage > 0) ? (
							<Col span={12}>
								{/* Only one can be larger than zero - either demurrage or despatch */}
								{
									cargoPort.estimatedDemurrage > 0 && (
										<Form.Item
											name="estimatedDemurrage"
											label={`Est. ${demurrageWord}`}
											valuePropName="v"
											colon={false}
											className={styles.formItemLabel}
											labelCol={{ span: 24 }}
										>
											<NumericInput
												data-testid="estimated-demurrage"
												key={cargoPort.id}
												value={round(cargoPort.estimatedDemurrage, 2)}
												disabled
												addonAfter={cargo.currency}
												placeholder={`Est ${demurrageWord.toLowerCase()}`}
											/>
										</Form.Item>
									)
								}
								{
									cargoPort.estimatedDespatch > 0 && (
										<Form.Item
											name="estimatedDespatch"
											label="Est. Despatch"
											valuePropName="v"
											colon={false}
											className={styles.formItemLabel}
											labelCol={{ span: 24 }}
										>
											<NumericInput
												key={cargoPort.id}
												value={round(cargoPort.estimatedDespatch, 2)}
												disabled
												addonAfter={cargo.currency}
												placeholder="Est despatch"
											/>
										</Form.Item>
									)
								}
							</Col>
						) : (
							<Col span={12}>
								<Form.Item
									name=""
									label={`Est. ${demurrageWord} / Despatch`}
									valuePropName="v"
									colon={false}
									className={styles.formItemLabel}
									labelCol={{ span: 24 }}
								>
									<Input
										key={cargoPort.id}
										disabled
										value={`No estimated ${demurrageWord.toLowerCase()} or despatch`}
										placeholder={`No estimated ${demurrageWord.toLowerCase()} or despatch`}
									/>
								</Form.Item>
							</Col>
						)}
					</>
				)}
				<Col span={12}>
					<Form.Item
						name="idleDays"
						label={'Idle days'}
						valuePropName="v"
						className={styles.formItemLabel}
						labelCol={{ span: 24 }}
						colon={false}
					>
						<NumericInput
							key={cargoPort.id}
							value={cargoPort.idleDays}
							onChange={(value) => handleUpdate('idleDays', value)}
							addonAfter="days"
							placeholder={'Idle days'}
							disabled={disabled || hasCargoRelet}
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name="totalTimeInPort"
						label="Total time in port"
						valuePropName="v"
						colon={false}
						className={styles.formItemLabel}
						labelCol={{ span: 24 }}
					>
						<NumericInput
							key={cargoPort.id}
							value={round(cargoPort.totalTimeInPort, 2)}
							disabled
							addonAfter={'days'}
							placeholder="Total time in port"
							data-testid='total-time-in-port'
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						valuePropName="v"
						name="linerCosts"
						label={`Est. Liner Costs / ${cargoUnit}`}
						className={styles.formItemLabel}
						labelCol={{ span: 24 }}
						colon={false}
					>
						<MultiCurrencyInput
							inputValues={{
								value: cargoPort.linerCostPricePerUnit ?? undefined,
								currency: cargoPort.linerCostCurrency,
								exchangeRate: cargoPort.linerCostExchangeRate,
							}}
							baseCurrency={cargo.currency}
							placeholder="Amount"
							wrapperClassName={styles.linerCostCurrencyWrapper}
							onChange={(values) => {
								handleCargoPortChange({
									cargoPortId: cargoPort.id,
									cargoId: cargo.id,
									attributes: {
										linerCostPricePerUnit: values.value,
										linerCostCurrency: values.currency,
										linerCostExchangeRate: values.exchangeRate,
									},
								});
							}}
							disabled={disabled}
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						valuePropName="v"
						name="linerCostUnit"
						label="Liner Cost Unit"
						className={styles.formItemLabel}
						labelCol={{ span: 24 }}
						colon={false}
					>
						<Select
							disabled={disabled}
							key={`${cargoPort.id}-linerCostUnit`}
							value={cargoPort.linerCostUnit ?? cargo.unit}
							options={[
								{
									key: 1,
									label: CargoUnitLabels[cargo.unit],
									value: cargo.unit,
								},
								...(cargo.secondaryUnit != null && cargo.secondaryUnit !== cargo.unit ? [{
									key: 2,
									label: CargoUnitLabels[cargo.secondaryUnit],
									value: cargo.secondaryUnit,
								}] : []),
								{
									key: 3,
									label: 'LS',
									value: LinerCostUnits.LS,
								},
							]}
							onSelect={(value) => {
								handleCargoPortChange({
									cargoPortId: cargoPort.id,
									cargoId: cargo.id,
									attributes: {
										linerCostUnit: value,
									},
								});
							}}
						/>
					</Form.Item>
				</Col>
			</Row>
		</>
	);
};

export default EstimatedCargoPortForm;
